import React, {useState, useMemo, useEffect} from 'react';
import {
    Plus,
    Trash2,
    Calendar,
    TrendingUp,
    Edit2,
    X,
    Pencil,
    Download,
    Upload,
    Save,
    Store,
    Settings,
    Globe
} from 'lucide-react';
import {createClient} from '@supabase/supabase-js';
import {processReceipt} from './documentAI';

// Initialize Supabase client
const supabaseUrl = 'https://reqqpcervoztukfjoshj.supabase.co'; // Replace with your URL
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJlcXFwY2Vydm96dHVrZmpvc2hqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjA0MjMyNjIsImV4cCI6MjA3NTk5OTI2Mn0.ZWh0WI8lKEh452o7aqonq364cumLlfQwMmu0IC5tpYM'; // Replace with your anon key
const supabase = createClient(supabaseUrl, supabaseKey);
export default function SpendingTracker() {
    const [user, setUser] = useState(null);

    // Country configurations
    const COUNTRY_CONFIG = {
        'GB': {
            name: 'United Kingdom',
            currency: '£',
            stores: ['Sainsbury\'s', 'Tesco', 'Asda', 'Morrisons', 'Lidl', 'Aldi', 'Waitrose', 'Co-op'],
            dateFormat: 'en-GB',
            categories: ['Food', 'Beverages', 'Household', 'Personal Care', 'Snacks', 'Other']
        },
        'US': {
            name: 'United States',
            currency: '$',
            stores: ['Walmart', 'Target', 'Kroger', 'Whole Foods', 'Costco', 'Safeway', 'Trader Joe\'s'],
            dateFormat: 'en-US',
            categories: ['Food', 'Beverages', 'Household', 'Personal Care', 'Snacks', 'Other']
        },
        'TR': {
            name: 'Turkey',
            currency: '₺',
            stores: ['Migros', 'Carrefour', 'BİM', 'A101', 'Şok', 'Metro', 'Macro Center'],
            dateFormat: 'tr-TR',
            categories: ['Gıda', 'İçecekler', 'Ev', 'Kişisel Bakım', 'Atıştırmalıklar', 'Diğer']
        },
        'DE': {
            name: 'Germany',
            currency: '€',
            stores: ['REWE', 'EDEKA', 'Lidl', 'Aldi', 'Kaufland', 'Netto', 'Penny'],
            dateFormat: 'de-DE',
            categories: ['Lebensmittel', 'Getränke', 'Haushalt', 'Körperpflege', 'Snacks', 'Sonstiges']
        },
        'FR': {
            name: 'France',
            currency: '€',
            stores: ['Carrefour', 'Auchan', 'Leclerc', 'Intermarché', 'Casino', 'Monoprix'],
            dateFormat: 'fr-FR',
            categories: ['Alimentation', 'Boissons', 'Ménage', 'Soins Personnels', 'Snacks', 'Autre']
        },
        'ES': {
            name: 'Spain',
            currency: '€',
            stores: ['Mercadona', 'Carrefour', 'Dia', 'Lidl', 'Alcampo', 'Eroski'],
            dateFormat: 'es-ES',
            categories: ['Alimentos', 'Bebidas', 'Hogar', 'Cuidado Personal', 'Aperitivos', 'Otro']
        },
        'IT': {
            name: 'Italy',
            currency: '€',
            stores: ['Esselunga', 'Conad', 'Coop', 'Carrefour', 'Lidl', 'Eurospin'],
            dateFormat: 'it-IT',
            categories: ['Alimentari', 'Bevande', 'Casa', 'Cura Personale', 'Snack', 'Altro']
        },
        'CA': {
            name: 'Canada',
            currency: 'C$',
            stores: ['Loblaws', 'Sobeys', 'Metro', 'Costco', 'Walmart', 'No Frills'],
            dateFormat: 'en-CA',
            categories: ['Food', 'Beverages', 'Household', 'Personal Care', 'Snacks', 'Other']
        },
        'AU': {
            name: 'Australia',
            currency: 'A$',
            stores: ['Woolworths', 'Coles', 'Aldi', 'IGA', 'Costco'],
            dateFormat: 'en-AU',
            categories: ['Food', 'Beverages', 'Household', 'Personal Care', 'Snacks', 'Other']
        },
        'NL': {
            name: 'Netherlands',
            currency: '€',
            stores: ['Albert Heijn', 'Jumbo', 'Lidl', 'Aldi', 'Plus', 'SPAR'],
            dateFormat: 'nl-NL',
            categories: ['Voedsel', 'Dranken', 'Huishouden', 'Persoonlijke Verzorging', 'Snacks', 'Overig']
        }
    };

    const TRANSLATIONS = {
        'en-GB': {
            appTitleText: 'Spending Tracker',
            logoutText: 'Logout',
            settingsText: 'Settings',
            backupExportText: 'Backup & Export',
            backupJSONText: 'Backup (JSON)',
            exportCSVText: 'Export CSV',
            restoreBackupText: 'Restore Backup',
            manageSavedItemsText: 'Manage Saved Items',
            manageStoresText: 'Manage Stores',
            manageCategoriesText: 'Manage Categories',
            countryText: 'Country',
            currencyText: 'Currency',
            closeText: 'Close',
            thisMonthText: 'This Month',
            thisWeekText: 'This Week',
            totalReceiptsText: 'Total Receipts',
            allTimeText: 'All Time',
            avgText: 'Avg',
            dayText: '/day',
            spendingByCategoryText: 'Spending by Category',
            addNewReceiptText: 'Add New Receipt',
            receiptsText: 'Receipts',
            weekText: 'Week',
            monthText: 'Month',
            allText: 'All',
            sortText: 'Sort',
            newestFirstText: 'Newest First',
            oldestFirstText: 'Oldest First',
            storeNameAZText: 'Store Name (A-Z)',
            storeNameZAText: 'Store Name (Z-A)',
            highestSpendingText: 'Highest Spending',
            lowestSpendingText: 'Lowest Spending',
            startDateText: 'Start Date',
            endDateText: 'End Date',
            clearDateFilterText: 'Clear Date Filter',
            noReceiptsText: 'No receipts yet. Add your first receipt above!',
            previousText: 'Previous',
            nextText: 'Next',
            showingText: 'Showing',
            toText: 'to',
            ofText: 'of',
            noStoreNameText: 'No store name',
            editReceiptText: 'Edit receipt',
            deleteReceiptText: 'Delete receipt',
            newReceiptText: 'New Receipt',
            editReceiptTitleText: 'Edit Receipt',
            dateTimeText: 'Date & Time',
            storeText: 'Store',
            selectStoreText: 'Select a store...',
            addNewStoreText: 'Add new store',
            newStoreNameText: 'New store name',
            addText: 'Add',
            cancelText: 'Cancel',
            addItemsText: 'Add Items',
            newItemText: 'New Item',
            previousItemsText: 'Previous Items',
            selectSavedItemText: 'Select saved item',
            chooseItemText: 'Choose an item...',
            noSavedItemsText: 'No saved items yet. Add some items first and they\'ll appear here for quick selection!',
            itemNameText: 'Item name',
            priceText: 'Price',
            addItemText: 'Add Item',
            onSaleText: 'On Sale',
            regularPriceText: 'Regular Price',
            salePriceNoteText: 'Sale price won\'t update the saved item price',
            totalText: 'Total',
            saveReceiptText: 'Save Receipt',
            updateReceiptText: 'Update Receipt',
            scanReceiptPhotoText: 'Scan Receipt Photo',
            selectStoreFirstText: 'Please select a store first to use photo scanning',
            uploadReceiptPhotoText: 'Upload Receipt Photo',
            takePhotoText: 'Take Photo',
            processingImageText: 'Processing receipt image...',
            supportedStoresText: 'This is an experimental feature and might not support every store receipt!',
            reviewScannedItemsText: 'Review Scanned Items',
            reviewWarningText: 'Please review and correct any mistakes before adding to receipt',
            noItemsDetectedText: 'No items detected. Please try again or add items manually.',
            addItemsToReceiptText: 'Add',
            itemsToReceiptText: 'Items to Receipt',
            noSavedItemsYetText: 'No saved items yet.',
            itemsText: 'items',
            addNewCategoryText: 'Add New Category',
            categoryNameText: 'Category name',
            currentCategoriesText: 'Current Categories',
            cannotDeleteLastCategoryText: 'Cannot delete the last category',
            deleteCategoryText: 'Delete category',
            categoriesText: 'categories',
            currentStoresText: 'Current Stores',
            storesText: 'stores',
            loginText: 'Login',
            signUpText: 'Sign Up',
            emailText: 'Email',
            passwordText: 'Password',
            loadingText: 'Loading...',
            cannotDeleteDefaultStoreText: 'Cannot delete default store',
            defaultText: 'Default',
            clickToViewFullSizeText: 'Click to view full size'
        },
        'tr-TR': {
            appTitleText: 'Harcama Takibi',
            logoutText: 'Çıkış',
            settingsText: 'Ayarlar',
            backupExportText: 'Yedekleme ve Dışa Aktarma',
            backupJSONText: 'Yedekle (JSON)',
            exportCSVText: 'CSV Dışa Aktar',
            restoreBackupText: 'Yedeği Geri Yükle',
            manageSavedItemsText: 'Kayıtlı Ürünleri Yönet',
            manageStoresText: 'Mağazaları Yönet',
            manageCategoriesText: 'Kategorileri Yönet',
            countryText: 'Ülke',
            currencyText: 'Para Birimi',
            closeText: 'Kapat',
            thisMonthText: 'Bu Ay',
            thisWeekText: 'Bu Hafta',
            totalReceiptsText: 'Toplam Fiş',
            allTimeText: 'Tüm Zamanlar',
            avgText: 'Ort',
            dayText: '/gün',
            spendingByCategoryText: 'Kategoriye Göre Harcama',
            addNewReceiptText: 'Yeni Fiş Ekle',
            receiptsText: 'Fişler',
            weekText: 'Hafta',
            monthText: 'Ay',
            allText: 'Tümü',
            sortText: 'Sırala',
            newestFirstText: 'Önce En Yeni',
            oldestFirstText: 'Önce En Eski',
            storeNameAZText: 'Mağaza Adı (A-Z)',
            storeNameZAText: 'Mağaza Adı (Z-A)',
            highestSpendingText: 'En Yüksek Harcama',
            lowestSpendingText: 'En Düşük Harcama',
            startDateText: 'Başlangıç Tarihi',
            endDateText: 'Bitiş Tarihi',
            clearDateFilterText: 'Tarih Filtresini Temizle',
            noReceiptsText: 'Henüz fiş yok. Yukarıdan ilk fişinizi ekleyin!',
            previousText: 'Önceki',
            nextText: 'Sonraki',
            showingText: 'Gösterilen',
            toText: '-',
            ofText: '/',
            noStoreNameText: 'Mağaza adı yok',
            editReceiptText: 'Fişi düzenle',
            deleteReceiptText: 'Fişi sil',
            newReceiptText: 'Yeni Fiş',
            editReceiptTitleText: 'Fişi Düzenle',
            dateTimeText: 'Tarih ve Saat',
            storeText: 'Mağaza',
            selectStoreText: 'Bir mağaza seçin...',
            addNewStoreText: 'Yeni mağaza ekle',
            newStoreNameText: 'Yeni mağaza adı',
            addText: 'Ekle',
            cancelText: 'İptal',
            addItemsText: 'Ürün Ekle',
            newItemText: 'Yeni Ürün',
            previousItemsText: 'Önceki Ürünler',
            selectSavedItemText: 'Kayıtlı ürün seç',
            chooseItemText: 'Bir ürün seçin...',
            noSavedItemsText: 'Henüz kayıtlı ürün yok. Önce birkaç ürün ekleyin, hızlı seçim için burada görünecekler!',
            itemNameText: 'Ürün adı',
            priceText: 'Fiyat',
            addItemText: 'Ürün Ekle',
            onSaleText: 'İndirimde',
            regularPriceText: 'Normal Fiyat',
            salePriceNoteText: 'İndirim fiyatı kayıtlı ürün fiyatını güncellemez',
            totalText: 'Toplam',
            saveReceiptText: 'Fişi Kaydet',
            updateReceiptText: 'Fişi Güncelle',
            scanReceiptPhotoText: 'Fiş Fotoğrafı Tara',
            selectStoreFirstText: 'Fotoğraf taramayı kullanmak için önce bir mağaza seçin',
            uploadReceiptPhotoText: 'Fiş Fotoğrafı Yükle',
            takePhotoText: 'Fotoğraf Çek',
            processingImageText: 'Fiş görüntüsü işleniyor...',
            supportedStoresText: 'Bu deneysel bir özelliktir, her mağazanın fişini algılamayabilir!',
            reviewScannedItemsText: 'Taranan Ürünleri İncele',
            reviewWarningText: 'Fişe eklemeden önce lütfen hataları gözden geçirin ve düzeltin',
            noItemsDetectedText: 'Ürün algılanmadı. Lütfen tekrar deneyin veya ürünleri manuel olarak ekleyin.',
            addItemsToReceiptText: 'Fişe',
            itemsToReceiptText: 'Ürün Ekle',
            noSavedItemsYetText: 'Henüz kayıtlı ürün yok.',
            itemsText: 'ürün',
            addNewCategoryText: 'Yeni Kategori Ekle',
            categoryNameText: 'Kategori adı',
            currentCategoriesText: 'Mevcut Kategoriler',
            cannotDeleteLastCategoryText: 'Son kategori silinemez',
            deleteCategoryText: 'Kategoriyi sil',
            categoriesText: 'kategori',
            currentStoresText: 'Mevcut Mağazalar',
            storesText: 'mağaza',
            loginText: 'Giriş Yap',
            signUpText: 'Kayıt Ol',
            emailText: 'E-posta',
            passwordText: 'Şifre',
            loadingText: 'Yükleniyor...',
            cannotDeleteDefaultStoreText: 'Varsayılan mağaza silinemez',
            defaultText: 'Varsayılan',
            clickToViewFullSizeText: 'Tam boyutta görmek için tıklayın'
        },
    }
    // Load data from memory on initial load
    // Load receipts from Supabase
    const loadReceipts = async () => {
        if (!user) return;
        try {
            const {data, error} = await supabase
                .from('receipts')
                .select('*')
                .eq('user_id', user.id)
                .order('date', {ascending: false});

            if (error) throw error;
            setReceipts(data || []);
        } catch (error) {
            console.error('Error loading receipts:', error);
        }
    };

// Load saved items from Supabase
    const loadSavedItems = async () => {
        if (!user) return;
        try {
            const {data, error} = await supabase
                .from('saved_items')
                .select('*')
                .eq('user_id', user.id);

            if (error) throw error;
            setSavedItems(data.map(item => ({
                name: item.name,
                category: item.category,
                defaultPrice: parseFloat(item.default_price)
            })));
        } catch (error) {
            console.error('Error loading saved items:', error);
        }
    };

    // Load user settings from Supabase
    const loadSettings = async () => {
        if (!user) return;
        try {
            const {data, error} = await supabase
                .from('user_settings')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (error && error.code !== 'PGRST116') throw error;

            if (data) {
                const country = data.country || 'GB';
                setSelectedCountry(country);

                // Combine default stores with custom stores
                const defaultStores = COUNTRY_CONFIG[country].stores;
                const userCustomStores = data.custom_stores || [];
                setCustomStores(userCustomStores);
                setSavedStores([...defaultStores, ...userCustomStores]);

                setCategories(data.categories || COUNTRY_CONFIG[country].categories);
                setCurrency(data.currency || COUNTRY_CONFIG[country].currency);
            } else {
                // Create default settings
                await supabase.from('user_settings').insert({
                    user_id: user.id,
                    country: 'GB',
                    custom_stores: [],
                    categories: COUNTRY_CONFIG['GB'].categories,
                    currency: '£'
                });
            }
        } catch (error) {
            console.error('Error loading settings:', error);
        }
    };

    // Load all data when user logs in
    useEffect(() => {
        if (user) {
            loadReceipts();
            loadSavedItems();
            loadSettings();
        } else {
            // Clear data when logged out
            setReceipts([]);
            setSavedItems([]);
            setCustomStores([]);
            setSavedStores(COUNTRY_CONFIG['GB'].stores);
            setCategories(COUNTRY_CONFIG['GB'].categories);
            setCurrency('£');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'store-az', 'store-za', 'high-low', 'low-high'
    const [showSortMenu, setShowSortMenu] = useState(false);

    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
    const [receipts, setReceipts] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentReceipt, setCurrentReceipt] = useState({
        date: new Date().toISOString().slice(0, 16),
        store: '',
        items: []
    });
    const [currentItem, setCurrentItem] = useState({
        name: '',
        price: '',
        category: 'Food'
    });
    const [viewMode, setViewMode] = useState('month');
    const [savedItems, setSavedItems] = useState([]);
    const [savedStores, setSavedStores] = useState(['Sainsbury\'s', 'Tesco', 'Asda', 'Morrisons', 'Lidl', 'Aldi']);
    const [useNewItem, setUseNewItem] = useState(true);
    const [selectedSavedItem, setSelectedSavedItem] = useState('');
    const [isOnSale, setIsOnSale] = useState(false);
    const [showEditItems, setShowEditItems] = useState(false);
    const [showAddStore, setShowAddStore] = useState(false);
    const [newStoreName, setNewStoreName] = useState('');
    const [categories, setCategories] = useState(['Food', 'Beverages', 'Household', 'Personal Care', 'Snacks', 'Other']);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingReceiptId, setEditingReceiptId] = useState(null);
    const [showBackupMenu, setShowBackupMenu] = useState(false);
    const [showManageStores, setShowManageStores] = useState(false);
    const [currency, setCurrency] = useState('£');
    const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const receiptsPerPage = 5;
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [parsedReceiptData, setParsedReceiptData] = useState(null);
    const [showReviewScreen, setShowReviewScreen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('GB');
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [savedItemsPage, setSavedItemsPage] = useState(1);
    const [storesPage, setStoresPage] = useState(1);
    const itemsPerPage = 10;
    const [categoriesPage, setCategoriesPage] = useState(1);
    // Get translations based on selected country
    const t = TRANSLATIONS[COUNTRY_CONFIG[selectedCountry].dateFormat] || TRANSLATIONS['en-GB'];
    const [customStores, setCustomStores] = useState([]);
    const [viewReceiptImage, setViewReceiptImage] = useState(null);
    const [currentReceiptImage, setCurrentReceiptImage] = useState(null);
    
    // Reset to page 1 when view mode or date filters change
    // Reset to page 1 when view mode, date filters, or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [viewMode, startDate, endDate, sortBy]);

    const handleCountryChange = async (countryCode) => {
        const config = COUNTRY_CONFIG[countryCode];
        setSelectedCountry(countryCode);
        setCurrency(config.currency);

        // Combine default stores for new country with user's custom stores
        const newStores = [...config.stores, ...customStores];
        setSavedStores(newStores);

        // Update categories to the new country's default categories
        setCategories(config.categories);

        await updateSettings({
            country: countryCode,
            currency: config.currency,
            categories: config.categories
        });
    };

    const addItemToReceipt = async () => {
        if (currentItem.name && currentItem.price) {
            const newItem = {...currentItem, price: parseFloat(currentItem.price)};
            setCurrentReceipt({
                ...currentReceipt,
                items: [...currentReceipt.items, newItem]
            });

            // Save to Supabase if not on sale
            if (!isOnSale) {
                try {
                    const {error} = await supabase
                        .from('saved_items')
                        .upsert({
                            user_id: user.id,
                            name: newItem.name,
                            category: newItem.category,
                            default_price: newItem.price
                        }, {
                            onConflict: 'user_id,name,category'
                        });

                    if (error) throw error;
                    await loadSavedItems(); // Reload items
                } catch (error) {
                    console.error('Error saving item:', error);
                }
            }

            setCurrentItem({name: '', price: '', category: 'Food'});
            setSelectedSavedItem('');
            setIsOnSale(false);
        }
    };

    const selectSavedItem = (itemKey) => {
        const [name, category] = itemKey.split('|||');
        const item = savedItems.find(i => i.name === name && i.category === category);
        if (item) {
            setCurrentItem({
                name: item.name,
                price: item.defaultPrice.toString(),
                category: item.category
            });
        }
    };

    const updateSavedItemPrice = async (itemName, itemCategory, newPrice) => {
        try {
            const {error} = await supabase
                .from('saved_items')
                .update({default_price: parseFloat(newPrice)})
                .eq('user_id', user.id)
                .eq('name', itemName)
                .eq('category', itemCategory);

            if (error) throw error;

            setSavedItems(savedItems.map(item =>
                (item.name === itemName && item.category === itemCategory)
                    ? {...item, defaultPrice: parseFloat(newPrice)}
                    : item
            ));
        } catch (error) {
            alert('Error updating item: ' + error.message);
        }
    };

    const deleteSavedItem = async (itemName, itemCategory) => {
        try {
            const {error} = await supabase
                .from('saved_items')
                .delete()
                .eq('user_id', user.id)
                .eq('name', itemName)
                .eq('category', itemCategory);

            if (error) throw error;

            setSavedItems(savedItems.filter(item =>
                !(item.name === itemName && item.category === itemCategory)
            ));
        } catch (error) {
            alert('Error deleting item: ' + error.message);
        }
    };

    const deleteSavedStore = async (storeName) => {
        const defaultStores = COUNTRY_CONFIG[selectedCountry].stores;

        // Check if it's a default store
        if (defaultStores.includes(storeName)) {
            alert(t.cannotDeleteDefaultStoreText || 'Cannot delete default store');
            return;
        }

        // It's a custom store, so we can delete it
        const newCustomStores = customStores.filter(store => store !== storeName);
        setCustomStores(newCustomStores);
        setSavedStores([...defaultStores, ...newCustomStores]);

        await updateSettings({custom_stores: newCustomStores});

        if (currentReceipt.store === storeName) {
            setCurrentReceipt({...currentReceipt, store: ''});
        }
    };

    const addNewStore = async () => {
        if (newStoreName && !savedStores.includes(newStoreName)) {
            const newCustomStores = [...customStores, newStoreName];
            setCustomStores(newCustomStores);

            const defaultStores = COUNTRY_CONFIG[selectedCountry].stores;
            setSavedStores([...defaultStores, ...newCustomStores]);

            await updateSettings({custom_stores: newCustomStores});

            setCurrentReceipt({...currentReceipt, store: newStoreName});
            setNewStoreName('');
            setShowAddStore(false);
        }
    };

    const addNewCategory = async () => {
        if (newCategoryName && !categories.includes(newCategoryName)) {
            const newCategories = [...categories, newCategoryName];
            setCategories(newCategories);
            await updateSettings({categories: newCategories});
            setNewCategoryName('');
        }
    };

    const deleteCategory = async (categoryName) => {
        if (categories.length > 1) {
            const newCategories = categories.filter(cat => cat !== categoryName);
            setCategories(newCategories);
            await updateSettings({categories: newCategories});
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        setUploadedImage(file);
        setIsProcessingImage(true);

        try {
            // Convert file to base64 for storage
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64Image = e.target.result.split(',')[1];
                setCurrentReceiptImage(base64Image);
            };
            reader.readAsDataURL(file);

            // Call Document AI service
            const result = await processReceipt(file);

            // Parse the response
            const parsedItems = parseDocumentAIResponse(result);

            setParsedReceiptData(parsedItems);
            setShowReviewScreen(true);
        } catch (error) {
            alert('Failed to process receipt: ' + error.message);
            setCurrentReceiptImage(null);
        } finally {
            setIsProcessingImage(false);
            setUploadedImage(null);
        }
    };

    const handleManualImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        // Convert file to base64
        const reader = new FileReader();
        reader.onload = (e) => {
            const base64Image = e.target.result.split(',')[1];
            setCurrentReceiptImage(base64Image);
        };
        reader.readAsDataURL(file);

        event.target.value = '';
    };
    
    const parseDocumentAIResponse = (aiResponse) => {
        const items = [];

        if (!aiResponse.entities) {
            return items;
        }

        console.log('Parsing Document AI response...');
        console.log('Total entities:', aiResponse.entities.length);

        // Separate items and prices based on entity type
        const itemNames = [];
        const itemPrices = [];

        aiResponse.entities.forEach(entity => {
            console.log('Entity:', entity.type, '-', entity.mentionText);

            // Your Custom Extractor uses these field names (lowercase)
            if (entity.type === 'items' || entity.type === 'Items') {
                itemNames.push(entity.mentionText);
            } else if (entity.type === 'prices' || entity.type === 'Prices') {
                // Clean up price - remove currency symbols and parse
                const priceText = entity.mentionText.replace(/[£$€\s]/g, '');
                const price = parseFloat(priceText);
                if (!isNaN(price)) {
                    itemPrices.push(price);
                }
            }
        });

        console.log('Found item names:', itemNames.length);
        console.log('Found prices:', itemPrices.length);

        // Match items with prices by position
        const minLength = Math.min(itemNames.length, itemPrices.length);

        for (let i = 0; i < minLength; i++) {
            items.push({
                name: itemNames[i].trim(),
                price: itemPrices[i],
                category: 'Food' // Default category
            });
        }

        // Handle unmatched items (items without prices)
        for (let i = minLength; i < itemNames.length; i++) {
            console.warn('Item without price:', itemNames[i]);
            // Optionally add with price 0 or skip
            // items.push({
            //   name: itemNames[i].trim(),
            //   price: 0,
            //   category: 'Food'
            // });
        }

        // Handle unmatched prices (prices without items)
        for (let i = minLength; i < itemPrices.length; i++) {
            console.warn('Price without item:', itemPrices[i]);
        }

        console.log('Parsed items:', items.length);

        return items;
    };

    const startEditingReceipt = (receipt) => {
        setEditingReceiptId(receipt.id);
        setCurrentReceipt({
            date: receipt.date,
            store: receipt.store,
            items: [...receipt.items]
        });
        setCurrentReceiptImage(receipt.image || null);
        setShowAddForm(true);
    };

    const updateReceipt = async () => {
        if (currentReceipt.items.length > 0) {
            try {
                const updatedReceipt = {
                    ...currentReceipt,
                    id: editingReceiptId,
                    user_id: user.id,
                    image: currentReceiptImage || null
                };

                const {error} = await supabase
                    .from('receipts')
                    .update(updatedReceipt)
                    .eq('id', editingReceiptId)
                    .eq('user_id', user.id);

                if (error) throw error;

                setReceipts(receipts.map(r =>
                    r.id === editingReceiptId ? updatedReceipt : r
                ));

                setCurrentReceipt({date: new Date().toISOString().slice(0, 16), store: '', items: []});
                setCurrentReceiptImage(null);
                setShowAddForm(false);
                setEditingReceiptId(null);
            } catch (error) {
                alert('Error updating receipt: ' + error.message);
            }
        }
    };

    const cancelEdit = () => {
        setCurrentReceipt({date: new Date().toISOString().slice(0, 16), store: '', items: []});
        setCurrentReceiptImage(null);
        setShowAddForm(false);
        setEditingReceiptId(null);
    };

    // Export all data as JSON
    const exportToJSON = () => {
        const data = {
            receipts,
            savedItems,
            savedStores,
            customStores,
            categories,
            currency,
            country: selectedCountry,
            exportDate: new Date().toISOString()
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `spending-tracker-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Export receipts to CSV
    const exportToCSV = () => {
        let csv = 'Date,Time,Store,Item,Category,Price\n';
        receipts.forEach(receipt => {
            const date = new Date(receipt.date);
            const dateStr = date.toLocaleDateString(COUNTRY_CONFIG[selectedCountry].dateFormat);
            const timeStr = date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
            receipt.items.forEach(item => {
                csv += `"${dateStr}","${timeStr}","${receipt.store}","${item.name}","${item.category}",${item.price}\n`;
            });
        });

        const blob = new Blob([csv], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `spending-tracker-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Import data from JSON file
    const importFromJSON = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = JSON.parse(e.target.result);

                    // Import receipts (remove id to let Supabase generate new ones)
                    if (data.receipts && data.receipts.length > 0) {
                        const receiptsWithUserId = data.receipts.map((receipt, index) => {
                            const { id, ...receiptWithoutId } = receipt; // Remove the old id
                            return {
                                ...receiptWithoutId,
                                id: Date.now() + index, // Generate new unique ID (integer only)
                                user_id: user.id
                            };
                        });

                        const { error: receiptsError } = await supabase
                            .from('receipts')
                            .insert(receiptsWithUserId);

                        if (receiptsError) throw receiptsError;
                    }

                    // Import saved items
                    if (data.savedItems && data.savedItems.length > 0) {
                        const itemsWithUserId = data.savedItems.map(item => ({
                            user_id: user.id,
                            name: item.name,
                            category: item.category,
                            default_price: item.defaultPrice
                        }));

                        const { error: itemsError } = await supabase
                            .from('saved_items')
                            .upsert(itemsWithUserId, {
                                onConflict: 'user_id,name,category'
                            });

                        if (itemsError) throw itemsError;
                    }

                    // Import settings
                    const settingsUpdate = {};

                    // Handle custom stores from backup
                    if (data.savedStores) {
                        // Determine which stores are custom (not in any default store list)
                        const allDefaultStores = Object.values(COUNTRY_CONFIG).flatMap(config => config.stores);
                        const customStoresFromBackup = data.savedStores.filter(store =>
                            !allDefaultStores.includes(store)
                        );
                        settingsUpdate.custom_stores = customStoresFromBackup;
                    }

                    if (data.categories) settingsUpdate.categories = data.categories;
                    if (data.currency) settingsUpdate.currency = data.currency;
                    if (data.country) settingsUpdate.country = data.country;

                    if (Object.keys(settingsUpdate).length > 0) {
                        await updateSettings(settingsUpdate);
                    }

                    // Reload all data from Supabase
                    await loadReceipts();
                    await loadSavedItems();
                    await loadSettings();

                    alert('Data imported successfully!');
                } catch (error) {
                    alert('Error importing file: ' + error.message);
                    console.error('Import error:', error);
                }
            };
            reader.readAsText(file);
        }
        event.target.value = '';
    };

    const removeItemFromReceipt = (index) => {
        setCurrentReceipt({
            ...currentReceipt,
            items: currentReceipt.items.filter((_, i) => i !== index)
        });
    };

    const saveReceipt = async () => {
        if (currentReceipt.items.length > 0) {
            try {
                const newReceipt = {
                    ...currentReceipt,
                    id: Date.now(),
                    user_id: user.id,
                    image: currentReceiptImage || null
                };

                const {error} = await supabase
                    .from('receipts')
                    .insert([newReceipt]);

                if (error) throw error;

                // Update local state
                setReceipts([...receipts, newReceipt]);

                // Save new store to settings if needed
                if (currentReceipt.store && !savedStores.includes(currentReceipt.store)) {
                    const newStores = [...savedStores, currentReceipt.store];
                    setSavedStores(newStores);
                    await updateSettings({saved_stores: newStores});
                }

                setCurrentReceipt({date: new Date().toISOString().slice(0, 16), store: '', items: []});
                setCurrentReceiptImage(null);
                setShowAddForm(false);
            } catch (error) {
                alert('Error saving receipt: ' + error.message);
            }
        }
    };

    const deleteReceipt = async (id) => {
        try {
            const {error} = await supabase
                .from('receipts')
                .delete()
                .eq('id', id)
                .eq('user_id', user.id);

            if (error) throw error;

            setReceipts(receipts.filter(r => r.id !== id));
        } catch (error) {
            alert('Error deleting receipt: ' + error.message);
        }
    };

    const updateSettings = async (updates) => {
        if (!user) return;
        try {
            // First check if settings exist
            const {data: existingSettings} = await supabase
                .from('user_settings')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (existingSettings) {
                // Update existing settings
                const {error} = await supabase
                    .from('user_settings')
                    .update(updates)
                    .eq('user_id', user.id);

                if (error) throw error;
            } else {
                // Create new settings with updates
                const {error} = await supabase
                    .from('user_settings')
                    .insert({
                        user_id: user.id,
                        ...updates
                    });

                if (error) throw error;
            }

            // Update local state
            if (updates.country !== undefined) setSelectedCountry(updates.country);
            if (updates.currency !== undefined) setCurrency(updates.currency);
            if (updates.saved_stores !== undefined) setSavedStores(updates.saved_stores);
            if (updates.categories !== undefined) setCategories(updates.categories);
        } catch (error) {
            console.error('Error updating settings:', error);
        }
    };

    const stats = useMemo(() => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());

        const monthReceipts = receipts.filter(r => new Date(r.date) >= startOfMonth);
        const weekReceipts = receipts.filter(r => new Date(r.date) >= startOfWeek);

        const calculateTotal = (recs) => recs.reduce((sum, r) =>
            sum + r.items.reduce((s, i) => s + i.price, 0), 0
        );

        const monthTotal = calculateTotal(monthReceipts);
        const weekTotal = calculateTotal(weekReceipts);
        const daysInMonth = now.getDate();
        const daysInWeek = now.getDay() + 1;

        const categoryTotals = {};
        receipts.forEach(r => {
            r.items.forEach(item => {
                categoryTotals[item.category] = (categoryTotals[item.category] || 0) + item.price;
            });
        });

        return {
            monthTotal,
            weekTotal,
            monthAvg: daysInMonth > 0 ? monthTotal / daysInMonth : 0,
            weekAvg: daysInWeek > 0 ? weekTotal / daysInWeek : 0,
            categoryTotals
        };
    }, [receipts]);

    const filteredReceipts = useMemo(() => {
        const now = new Date();
        let filtered = receipts;

        if (viewMode === 'month') {
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            filtered = receipts.filter(r => new Date(r.date) >= startOfMonth);
        } else if (viewMode === 'week') {
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            startOfWeek.setHours(0, 0, 0, 0);
            filtered = receipts.filter(r => new Date(r.date) >= startOfWeek);
        } else if (viewMode === 'all') {
            if (startDate || endDate) {
                filtered = receipts.filter(r => {
                    const receiptDate = new Date(r.date);
                    const start = startDate ? new Date(startDate) : new Date(0);
                    const end = endDate ? new Date(endDate) : new Date(8640000000000000); // Max date
                    end.setHours(23, 59, 59, 999); // Include the entire end date
                    return receiptDate >= start && receiptDate <= end;
                });
            } else {
                // Show all receipts when no date filter is applied
                filtered = receipts;
            }
        }

        return filtered;
    }, [receipts, viewMode, startDate, endDate]);

    const paginatedReceipts = useMemo(() => {
        let sorted = [...filteredReceipts];

        // Sort based on selected option
        switch (sortBy) {
            case 'newest':
                sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
                break;
            case 'oldest':
                sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
                break;
            case 'store-az':
                sorted.sort((a, b) => (a.store || '').localeCompare(b.store || ''));
                break;
            case 'store-za':
                sorted.sort((a, b) => (b.store || '').localeCompare(a.store || ''));
                break;
            case 'high-low':
                sorted.sort((a, b) => {
                    const totalA = a.items.reduce((sum, i) => sum + i.price, 0);
                    const totalB = b.items.reduce((sum, i) => sum + i.price, 0);
                    return totalB - totalA;
                });
                break;
            case 'low-high':
                sorted.sort((a, b) => {
                    const totalA = a.items.reduce((sum, i) => sum + i.price, 0);
                    const totalB = b.items.reduce((sum, i) => sum + i.price, 0);
                    return totalA - totalB;
                });
                break;
            default:
                sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
        }

        const startIndex = (currentPage - 1) * receiptsPerPage;
        const endIndex = startIndex + receiptsPerPage;
        return sorted.slice(startIndex, endIndex);
    }, [filteredReceipts, currentPage, sortBy]);

    const totalPages = Math.ceil(filteredReceipts.length / receiptsPerPage);

    const paginatedSavedItems = useMemo(() => {
        const startIndex = (savedItemsPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return savedItems.slice(startIndex, endIndex);
    }, [savedItems, savedItemsPage]);

    const savedItemsTotalPages = Math.ceil(savedItems.length / itemsPerPage);

    const paginatedStores = useMemo(() => {
        const startIndex = (storesPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return savedStores.slice(startIndex, endIndex);
    }, [savedStores, storesPage]);

    const storesTotalPages = Math.ceil(savedStores.length / itemsPerPage);

    const paginatedCategories = useMemo(() => {
        const startIndex = (categoriesPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return categories.slice(startIndex, endIndex);
    }, [categories, categoriesPage]);

    const categoriesTotalPages = Math.ceil(categories.length / itemsPerPage);

    // Check if user is logged in
    useEffect(() => {
        supabase.auth.getSession().then(({data: {session}}) => {
            setUser(session?.user ?? null);
            setLoading(false);
        });

        const {data: {subscription}} = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
        });

        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showSortMenu && !event.target.closest('.relative')) {
                setShowSortMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSortMenu]);

    useEffect(() => {
        if (showEditItems) setSavedItemsPage(1);
    }, [showEditItems]);

    useEffect(() => {
        if (showManageStores) setStoresPage(1);
    }, [showManageStores]);

    useEffect(() => {
        if (showAddCategory) setCategoriesPage(1);
    }, [showAddCategory]);

// Sign up
    const signUp = async () => {
        try {
            const {error} = await supabase.auth.signUp({
                email,
                password,
            });
            if (error) throw error;
            alert('Check your email for the confirmation link!');
        } catch (error) {
            alert(error.message);
        }
    };

// Sign in
    const signIn = async () => {
        try {
            const {error} = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error) throw error;
        } catch (error) {
            alert(error.message);
        }
    };

// Sign out
    const signOut = async () => {
        const {error} = await supabase.auth.signOut();
        if (error) console.error('Error signing out:', error);
    };

    // Show loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-xl font-semibold text-gray-700">Loading...</div>
            </div>
        );
    }


// Show login/signup if not authenticated
    if (!user) {
        return (
            <div
                className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                    <div className="flex justify-center mb-4">
                        <img
                            src="/logo.png"
                            alt="Spending Tracker"
                            className="h-24 w-48 object-contain"
                        />
                    </div>

                    <div className="flex gap-2 mb-6">
                        <button
                            onClick={() => setAuthMode('login')}
                            className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                                authMode === 'login'
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                            {t.loginText}
                        </button>
                        <button
                            onClick={() => setAuthMode('signup')}
                            className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                                authMode === 'signup'
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                            {t.signUpText}
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t.emailText}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="your@email.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t.passwordText}</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            onClick={authMode === 'login' ? signIn : signUp}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-colors"
                        >
                            {authMode === 'login' ? t.loginText : t.signUpText}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                            <img
                                src="/logo.png"
                                alt="Spending Tracker"
                                className="h-12 object-contain"
                            />
                        </h1>
                        <div className="flex gap-2">
                            {/* Country Selector */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
                                    title="Country & Currency"
                                >
                                    <Globe size={20} className="text-gray-700"/>
                                    <span className="font-semibold">{currency}</span>
                                </button>
                                {showCurrencyMenu && (
                                    <div
                                        className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                        <div className="p-3 border-b border-gray-200">
                                            <label
                                                className="block text-sm font-medium text-gray-700 mb-2">{t.countryText}</label>
                                            <select
                                                value={selectedCountry}
                                                onChange={(e) => handleCountryChange(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            >
                                                {Object.entries(COUNTRY_CONFIG).map(([code, config]) => (
                                                    <option key={code} value={code}>{config.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="p-3">
                                            <label
                                                className="block text-sm font-medium text-gray-700 mb-2">{t.currencyText}</label>
                                            <select
                                                value={currency}
                                                onChange={async (e) => {
                                                    setCurrency(e.target.value);
                                                    await updateSettings({currency: e.target.value});
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            >
                                                {['£', '$', '€', '¥', '₹', '₺', 'kr', 'R$', 'A$', 'C$', '₱', '฿', '₩', '₪', 'zł', 'Kč', 'Ft', 'lei', 'CHF', 'S$', 'HK$', 'NZ$', 'R', 'Mex$'].map((curr) => (
                                                    <option key={curr} value={curr}>{curr}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <button
                                            onClick={() => setShowCurrencyMenu(false)}
                                            className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 text-sm text-gray-700 border-t border-gray-200 rounded-b-lg"
                                        >
                                            {t.closeText}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Backup Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowBackupMenu(!showBackupMenu)}
                                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Backup & Export"
                                >
                                    <Save size={20} className="text-gray-700"/>
                                </button>
                                {showBackupMenu && (
                                    <div
                                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                        <button
                                            onClick={() => {
                                                exportToJSON();
                                                setShowBackupMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                        >
                                            <Download size={16}/>
                                            {t.backupJSONText}
                                        </button>
                                        <button
                                            onClick={() => {
                                                exportToCSV();
                                                setShowBackupMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                                        >
                                            <Download size={16}/>
                                            {t.exportCSVText}
                                        </button>
                                        <label
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 cursor-pointer border-t border-gray-100 rounded-b-lg">
                                            <Upload size={16}/>
                                            {t.restoreBackupText}
                                            <input
                                                type="file"
                                                accept=".json"
                                                onChange={(e) => {
                                                    importFromJSON(e);
                                                    setShowBackupMenu(false);
                                                }}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* Settings Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Settings"
                                >
                                    <Settings size={20} className="text-gray-700"/>
                                </button>
                                {showSettingsMenu && (
                                    <div
                                        className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                        <button
                                            onClick={() => {
                                                setShowEditItems(true);
                                                setShowSettingsMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                        >
                                            <Edit2 size={16}/>
                                            {t.manageSavedItemsText}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowManageStores(true);
                                                setShowSettingsMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                                        >
                                            <Store size={16}/>
                                            {t.manageStoresText}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowAddCategory(true);
                                                setShowSettingsMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100 rounded-b-lg"
                                        >
                                            <Plus size={16}/>
                                            {t.manageCategoriesText}
                                        </button>

                                        {/* Logout */}
                                        <button
                                            onClick={signOut}
                                            className="w-full p-2 bg-red-100 hover:bg-red-200 rounded-lg items-center justify-center transition-colors"
                                            title="Logout"
                                        >
                                            <span className="text-red-700 font-semibold px-2">{t.logoutText}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Stats Dashboard */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
                            <div className="text-sm opacity-90">{t.thisMonthText}</div>
                            <div className="text-2xl font-bold">{currency}{stats.monthTotal.toFixed(2)}</div>
                            <div
                                className="text-xs opacity-75 mt-1">{t.avgText}: {currency}{stats.monthAvg.toFixed(2)}/{t.dayText}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-4 text-white">
                            <div className="text-sm opacity-90">{t.thisWeekText}</div>
                            <div className="text-2xl font-bold">{currency}{stats.weekTotal.toFixed(2)}</div>
                            <div
                                className="text-xs opacity-75 mt-1">{t.avgText}: {currency}{stats.weekAvg.toFixed(2)}/{t.dayText}</div>
                        </div>
                        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
                            <div className="text-sm opacity-90">{t.totalReceiptsText}</div>
                            <div className="text-2xl font-bold">{receipts.length}</div>
                        </div>
                        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white">
                            <div className="text-sm opacity-90">{t.allTimeText}</div>
                            <div className="text-2xl font-bold">{currency}{receipts.reduce((sum, r) =>
                                sum + r.items.reduce((s, i) => s + i.price, 0), 0).toFixed(2)}</div>
                        </div>
                    </div>

                    {/* Category Breakdown */}
                    {Object.keys(stats.categoryTotals).length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 mb-6">
                            <h2 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <TrendingUp size={20}/>
                                {t.spendingByCategoryText}
                            </h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.entries(stats.categoryTotals)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([cat, total]) => (
                                        <div key={cat} className="bg-white rounded-lg p-3 border border-gray-200">
                                            <div className="text-sm text-gray-600">{cat}</div>
                                            <div
                                                className="text-lg font-bold text-gray-800">{currency}{total.toFixed(2)}</div>
                                        </div>
                                    ))}
                            </div>
                        </div>)}

                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors mb-3"
                    >
                        <Plus size={20}/>
                        {t.addNewReceiptText}
                    </button>
                </div>

                {/* Manage Categories */}
                {showAddCategory && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">{t.manageCategoriesText}</h2>
                            <button
                                onClick={() => {
                                    setShowAddCategory(false);
                                    setCategoriesPage(1);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24}/>
                            </button>
                        </div>

                        <div className="mb-4">
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">{t.addNewCategoryText}</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    placeholder={t.categoryNameText}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    onKeyPress={(e) => e.key === 'Enter' && addNewCategory()}
                                />
                                <button
                                    onClick={addNewCategory}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                    {t.addText}
                                </button>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <h3 className="font-semibold text-gray-700 mb-3">{t.currentCategoriesText}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                                {paginatedCategories.map((cat, idx) => {
                                    const actualIndex = (categoriesPage - 1) * itemsPerPage + idx;
                                    return (
                                        <div key={actualIndex}
                                             className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <span className="font-medium">{cat}</span>
                                            <button
                                                onClick={() => deleteCategory(cat)}
                                                className="text-red-600 hover:text-red-700"
                                                disabled={categories.length === 1}
                                                title={categories.length === 1 ? "Cannot delete the last category" : "Delete category"}
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {categoriesTotalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={() => setCategoriesPage(prev => Math.max(1, prev - 1))}
                                        disabled={categoriesPage === 1}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.previousText}
                                    </button>

                                    <div className="flex gap-1">
                                        {[...Array(categoriesTotalPages)].map((_, idx) => {
                                            const pageNum = idx + 1;
                                            if (
                                                pageNum === 1 ||
                                                pageNum === categoriesTotalPages ||
                                                (pageNum >= categoriesPage - 1 && pageNum <= categoriesPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCategoriesPage(pageNum)}
                                                        className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                            categoriesPage === pageNum
                                                                ? 'bg-indigo-600 text-white'
                                                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            } else if (
                                                pageNum === categoriesPage - 2 ||
                                                pageNum === categoriesPage + 2
                                            ) {
                                                return <span key={pageNum}
                                                             className="px-2 py-2 text-gray-400">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCategoriesPage(prev => Math.min(categoriesTotalPages, prev + 1))}
                                        disabled={categoriesPage === categoriesTotalPages}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.nextText}
                                    </button>
                                </div>
                            )}

                            <div className="text-center text-sm text-gray-500 mt-3">
                                {t.showingText} {((categoriesPage - 1) * itemsPerPage) + 1} {t.toText} {Math.min(categoriesPage * itemsPerPage, categories.length)} {t.ofText} {categories.length} {t.categoriesText}
                            </div>
                        </div>
                    </div>
                )}

                {/* Manage Saved Items */}
                {showEditItems && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">{t.manageSavedItemsText}</h2>
                            <button
                                onClick={() => {
                                    setShowEditItems(false);
                                    setSavedItemsPage(1);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24}/>
                            </button>
                        </div>

                        {savedItems.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">{t.noSavedItemsYetText}</div>
                        ) : (
                            <>
                                <div className="space-y-2 mb-4">
                                    {paginatedSavedItems.map((item, idx) => {
                                        const actualIndex = (savedItemsPage - 1) * itemsPerPage + idx;
                                        return (
                                            <div key={actualIndex}
                                                 className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <div className="font-medium">{item.name}</div>
                                                    <div className="text-sm text-gray-500">{item.category}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-600">{currency}</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={item.defaultPrice}
                                                        onChange={(e) => updateSavedItemPrice(item.name, item.category, e.target.value)}
                                                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                    <button
                                                        onClick={() => deleteSavedItem(item.name, item.category)}
                                                        className="text-red-600 hover:text-red-700 ml-2"
                                                    >
                                                        <Trash2 size={18}/>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {savedItemsTotalPages > 1 && (
                                    <div
                                        className="flex justify-center items-center gap-2 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => setSavedItemsPage(prev => Math.max(1, prev - 1))}
                                            disabled={savedItemsPage === 1}
                                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                        >
                                            {t.previousText}
                                        </button>

                                        <div className="flex gap-1">
                                            {[...Array(savedItemsTotalPages)].map((_, idx) => {
                                                const pageNum = idx + 1;
                                                if (
                                                    pageNum === 1 ||
                                                    pageNum === savedItemsTotalPages ||
                                                    (pageNum >= savedItemsPage - 1 && pageNum <= savedItemsPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => setSavedItemsPage(pageNum)}
                                                            className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                                savedItemsPage === pageNum
                                                                    ? 'bg-indigo-600 text-white'
                                                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                            }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                } else if (
                                                    pageNum === savedItemsPage - 2 ||
                                                    pageNum === savedItemsPage + 2
                                                ) {
                                                    return <span key={pageNum}
                                                                 className="px-2 py-2 text-gray-400">...</span>;
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setSavedItemsPage(prev => Math.min(savedItemsTotalPages, prev + 1))}
                                            disabled={savedItemsPage === savedItemsTotalPages}
                                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                        >
                                            {t.nextText}
                                        </button>
                                    </div>
                                )}

                                <div className="text-center text-sm text-gray-500 mt-3">
                                    {t.showingText} {((savedItemsPage - 1) * itemsPerPage) + 1} {t.toText} {Math.min(savedItemsPage * itemsPerPage, savedItems.length)} {t.ofText} {savedItems.length} {t.itemsText}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Manage Stores */}
                {showManageStores && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">{t.manageStoresText}</h2>
                            <button
                                onClick={() => {
                                    setShowManageStores(false);
                                    setStoresPage(1);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24}/>
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t.addNewStoreText}</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newStoreName}
                                    onChange={(e) => setNewStoreName(e.target.value)}
                                    placeholder="Store name"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                    onKeyPress={async (e) => {
                                        if (e.key === 'Enter' && newStoreName && !savedStores.includes(newStoreName)) {
                                            const newCustomStores = [...customStores, newStoreName];
                                            setCustomStores(newCustomStores);

                                            const defaultStores = COUNTRY_CONFIG[selectedCountry].stores;
                                            setSavedStores([...defaultStores, ...newCustomStores]);

                                            await updateSettings({custom_stores: newCustomStores});
                                            setNewStoreName('');
                                        }
                                    }}
                                />
                                <button
                                    onClick={async () => {
                                        if (newStoreName && !savedStores.includes(newStoreName)) {
                                            const newCustomStores = [...customStores, newStoreName];
                                            setCustomStores(newCustomStores);

                                            const defaultStores = COUNTRY_CONFIG[selectedCountry].stores;
                                            setSavedStores([...defaultStores, ...newCustomStores]);

                                            await updateSettings({custom_stores: newCustomStores});
                                            setNewStoreName('');
                                        }
                                    }}
                                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                                >
                                    {t.addText}
                                </button>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <h3 className="font-semibold text-gray-700 mb-3">{t.currentStoresText}</h3>
                            <div className="space-y-2 mb-4">
                                {paginatedStores.map((store, idx) => {
                                    const actualIndex = (storesPage - 1) * itemsPerPage + idx;
                                    const isDefault = COUNTRY_CONFIG[selectedCountry].stores.includes(store);
                                    return (
                                        <div key={actualIndex}
                                             className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{store}</span>
                                                {isDefault && (
                                                    <span
                                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                                        {t.defaultText}
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => deleteSavedStore(store)}
                                                className={`text-red-600 hover:text-red-700 ${isDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                disabled={isDefault}
                                                title={isDefault ? t.cannotDeleteDefaultStoreText : ''}
                                            >
                                                <Trash2 size={18}/>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {storesTotalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={() => setStoresPage(prev => Math.max(1, prev - 1))}
                                        disabled={storesPage === 1}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.previousText}
                                    </button>

                                    <div className="flex gap-1">
                                        {[...Array(storesTotalPages)].map((_, idx) => {
                                            const pageNum = idx + 1;
                                            if (
                                                pageNum === 1 ||
                                                pageNum === storesTotalPages ||
                                                (pageNum >= storesPage - 1 && pageNum <= storesPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setStoresPage(pageNum)}
                                                        className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                            storesPage === pageNum
                                                                ? 'bg-indigo-600 text-white'
                                                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            } else if (
                                                pageNum === storesPage - 2 ||
                                                pageNum === storesPage + 2
                                            ) {
                                                return <span key={pageNum}
                                                             className="px-2 py-2 text-gray-400">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setStoresPage(prev => Math.min(storesTotalPages, prev + 1))}
                                        disabled={storesPage === storesTotalPages}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.nextText}
                                    </button>
                                </div>
                            )}

                            <div className="text-center text-sm text-gray-500 mt-3">
                                {t.showingText} {((storesPage - 1) * itemsPerPage) + 1} {t.toText} {Math.min(storesPage * itemsPerPage, savedStores.length)} {t.ofText} {savedStores.length} {t.storesText}
                            </div>
                        </div>
                    </div>
                )}

                {/* Review Scanned Receipt */}
                {showReviewScreen && parsedReceiptData && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-indigo-500">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">{t.reviewScannedItemsText}</h2>
                            <button
                                onClick={() => {
                                    setShowReviewScreen(false);
                                    setParsedReceiptData(null);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24}/>
                            </button>
                        </div>

                        <div
                            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                            ⚠️ {t.reviewWarningText}
                        </div>

                        {parsedReceiptData.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {t.noItemsDetectedText}
                            </div>
                        ) : (
                            <div className="space-y-3 mb-4">
                                {parsedReceiptData.map((item, idx) => (
                                    <div key={idx} className="flex gap-3 items-center p-3 bg-gray-50 rounded-lg">
                                        <input
                                            type="text"
                                            value={item.name}
                                            onChange={(e) => {
                                                const updated = [...parsedReceiptData];
                                                updated[idx].name = e.target.value;
                                                setParsedReceiptData(updated);
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Item name"
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.price}
                                            onChange={(e) => {
                                                const updated = [...parsedReceiptData];
                                                updated[idx].price = parseFloat(e.target.value) || 0;
                                                setParsedReceiptData(updated);
                                            }}
                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Price"
                                        />
                                        <select
                                            value={item.category}
                                            onChange={(e) => {
                                                const updated = [...parsedReceiptData];
                                                updated[idx].category = e.target.value;
                                                setParsedReceiptData(updated);
                                            }}
                                            className="px-3 py-2 border border-gray-300 rounded-lg"
                                        >
                                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                        <button
                                            onClick={() => {
                                                setParsedReceiptData(parsedReceiptData.filter((_, i) => i !== idx));
                                            }}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 size={18}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    // Add all reviewed items to current receipt
                                    setCurrentReceipt({
                                        ...currentReceipt,
                                        items: [...currentReceipt.items, ...parsedReceiptData]
                                    });
                                    setShowReviewScreen(false);
                                    setParsedReceiptData(null);
                                }}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors"
                                disabled={parsedReceiptData.length === 0}
                            >
                                {t.addText} {parsedReceiptData.length} {t.itemsToReceiptText}
                            </button>
                            <button
                                onClick={() => {
                                    setShowReviewScreen(false);
                                    setParsedReceiptData(null);
                                }}
                                className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
                            >
                                {t.cancelText}
                            </button>
                        </div>
                    </div>
                )}

                {/* Add Receipt Form */}
                {showAddForm && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">
                            {editingReceiptId ? t.editReceiptText : t.newReceiptText}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t.dateTimeText}</label>
                                <input
                                    type="datetime-local"
                                    value={currentReceipt.date}
                                    onChange={(e) => setCurrentReceipt({...currentReceipt, date: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t.storeText}</label>
                                <div className="flex gap-2">
                                    <select
                                        value={currentReceipt.store}
                                        onChange={(e) => setCurrentReceipt({...currentReceipt, store: e.target.value})}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                        <option value="">{t.selectStoreText}</option>
                                        {savedStores.map(store => (
                                            <option key={store} value={store}>{store}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => setShowAddStore(!showAddStore)}
                                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                                        title={t.addNewStoreText}
                                    >
                                        <Plus size={20}/>
                                    </button>
                                </div>
                                {showAddStore && (
                                    <div className="flex gap-2 mt-2">
                                        <input
                                            type="text"
                                            value={newStoreName}
                                            onChange={(e) => setNewStoreName(e.target.value)}
                                            placeholder={t.newStoreNameText}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            onKeyPress={(e) => e.key === 'Enter' && addNewStore()}
                                        />
                                        <button
                                            onClick={addNewStore}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                                        >
                                            {t.addText}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowAddStore(false);
                                                setNewStoreName('');
                                            }}
                                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                                        >
                                            <X size={20}/>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Photo Upload Option */}
                        <div className="border-b border-gray-200 pb-4 mb-4">
                            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span>📸</span>
                                {t.scanReceiptPhotoText}
                            </h3>

                            {!currentReceipt.store ? (
                                <div
                                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                                    {t.selectStoreFirstText}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <label
                                            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg cursor-pointer transition-colors flex items-center justify-center gap-2">
                                            <Upload size={20}/>
                                            {t.uploadReceiptPhotoText}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={isProcessingImage}
                                            />
                                        </label>

                                        <label
                                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg cursor-pointer transition-colors flex items-center justify-center gap-2">
                                            📷 {t.takePhotoText}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={isProcessingImage}
                                            />
                                        </label>
                                    </div>

                                    {isProcessingImage && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                                            <div
                                                className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                                            <p className="text-sm text-blue-800">{t.processingImageText}</p>
                                            {uploadedImage && (
                                                <div className="mt-3">
                                                    <img
                                                        src={URL.createObjectURL(uploadedImage)}
                                                        alt="Uploading..."
                                                        className="max-h-32 mx-auto rounded border border-blue-300"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="text-xs text-gray-500 text-center">
                                        {t.supportedStoresText}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Image Preview/Upload */}
                        <div className="mb-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Receipt Photo</label>
                                    {currentReceiptImage && (
                                        <button
                                            onClick={() => setCurrentReceiptImage(null)}
                                            className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                                        >
                                            <Trash2 size={14}/>
                                            Remove Photo
                                        </button>
                                    )}
                                </div>
                                {currentReceiptImage ? (
                                    <div className="relative">
                                        <img
                                            src={`data:image/jpeg;base64,${currentReceiptImage}`}
                                            alt="Receipt preview"
                                            className="w-full h-48 object-contain bg-white rounded border border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => setViewReceiptImage(currentReceiptImage)}
                                        />
                                        <div className="text-xs text-gray-500 mt-1 text-center">
                                            Click image to view full size
                                        </div>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                                        <Upload size={32} className="text-gray-400 mb-2"/>
                                        <span className="text-sm text-gray-500">Click to upload receipt photo</span>
                                        <span className="text-xs text-gray-400 mt-1">(Optional)</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleManualImageUpload}
                                            className="hidden"
                                        />
                                    </label>
                                )}
                            </div>
                        </div>
                        
                        <div className="border-t border-gray-200 pt-4 mb-4">
                            <h3 className="font-semibold text-gray-700 mb-3">{t.addItemsText}</h3>

                            <div className="flex gap-2 mb-3">
                                <button
                                    onClick={() => {
                                        setUseNewItem(true);
                                        setCurrentItem({name: '', price: '', category: 'Food'});
                                        setSelectedSavedItem('');
                                    }}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        useNewItem
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {t.newItemText}
                                </button>
                                <button
                                    onClick={() => setUseNewItem(false)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        !useNewItem
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {t.previousItemsText}
                                </button>
                            </div>

                            {!useNewItem && savedItems.length > 0 && (
                                <div className="mb-3">
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-1">{t.selectSavedItemText}</label>
                                    <select
                                        value={selectedSavedItem}
                                        onChange={(e) => {
                                            setSelectedSavedItem(e.target.value);
                                            selectSavedItem(e.target.value);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">{t.chooseItemText}</option>
                                        {savedItems.map((item, idx) => (
                                            <option key={idx} value={`${item.name}|||${item.category}`}>
                                                {item.name} - {item.category} ({currency}{item.defaultPrice.toFixed(2)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!useNewItem && savedItems.length === 0 && (
                                <div className="text-sm text-gray-500 mb-3 p-3 bg-gray-50 rounded-lg">
                                    {t.noSavedItemsText}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                                <input
                                    type="text"
                                    value={currentItem.name}
                                    onChange={(e) => setCurrentItem({...currentItem, name: e.target.value})}
                                    placeholder={t.itemNameText}
                                    disabled={!useNewItem && !selectedSavedItem}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                />
                                <input
                                    type="number"
                                    step="0.01"
                                    value={currentItem.price}
                                    onChange={(e) => setCurrentItem({...currentItem, price: e.target.value})}
                                    placeholder={t.priceText}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                />
                                <select
                                    value={currentItem.category}
                                    onChange={(e) => setCurrentItem({...currentItem, category: e.target.value})}
                                    disabled={!useNewItem && !selectedSavedItem}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                >
                                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                                <button
                                    onClick={addItemToReceipt}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                    {t.addItemText}
                                </button>
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                                <button
                                    onClick={() => setIsOnSale(!isOnSale)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isOnSale
                                            ? 'bg-orange-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {isOnSale ? '🏷️ ' + t.onSaleText : t.regularPriceText}
                                </button>
                                {isOnSale && (
                                    <span className="text-sm text-gray-600">
                    {t.salePriceNoteText}
                  </span>
                                )}
                            </div>

                            {currentReceipt.items.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    {currentReceipt.items.map((item, idx) => (
                                        <div key={idx}
                                             className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                                            <div className="flex-1">
                                                <span className="font-medium">{item.name}</span>
                                                <span className="text-sm text-gray-500 ml-2">({item.category})</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold">{currency}{item.price.toFixed(2)}</span>
                                                <button
                                                    onClick={() => removeItemFromReceipt(idx)}
                                                    className="text-red-600 hover:text-red-700"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="text-right mt-3 text-lg font-bold text-gray-800">
                                        {t.totalText}: {currency}{currentReceipt.items.reduce((sum, i) => sum + i.price, 0).toFixed(2)}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={editingReceiptId ? updateReceipt : saveReceipt}
                                disabled={currentReceipt.items.length === 0}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                            >
                                {editingReceiptId ? t.updateReceiptText : t.saveReceiptText}
                            </button>
                            <button
                                onClick={editingReceiptId ? cancelEdit : () => setShowAddForm(false)}
                                className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 rounded-lg transition-colors"
                            >
                                {t.cancelText}
                            </button>
                        </div>
                    </div>
                )}

                {/* Receipts List */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex flex-col gap-4 mb-4">
                        <div className="flex justify-between items-center flex-wrap gap-3">
                            <h2 className="text-xl font-bold text-gray-800">{t.receiptsText}</h2>

                            <div className="flex gap-2 items-center">
                                <div className="flex gap-2">
                                    {['week', 'month', 'all'].map(mode => (
                                        <button
                                            key={mode}
                                            onClick={() => setViewMode(mode)}
                                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                viewMode === mode
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {mode === 'week' ? t.weekText : mode === 'month' ? t.monthText : t.allText}
                                        </button>
                                    ))}
                                </div>

                                <div className="relative">
                                    <button
                                        onClick={() => setShowSortMenu(!showSortMenu)}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors flex items-center gap-2"
                                    >
                                        <span>{t.sortText}</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </button>

                                    {showSortMenu && (
                                        <div
                                            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                            <button
                                                onClick={() => {
                                                    setSortBy('newest');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 rounded-t-lg ${sortBy === 'newest' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.newestFirstText}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('oldest');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-t border-gray-100 ${sortBy === 'oldest' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.oldestFirstText}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('store-az');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-t border-gray-100 ${sortBy === 'store-az' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.storeNameAZText}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('store-za');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-t border-gray-100 ${sortBy === 'store-za' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.storeNameZAText}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('high-low');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-t border-gray-100 ${sortBy === 'high-low' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.highestSpendingText}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('low-high');
                                                    setShowSortMenu(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-t border-gray-100 rounded-b-lg ${sortBy === 'low-high' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
                                            >
                                                {t.lowestSpendingText}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {viewMode === 'all' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-1">{t.startDateText}</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-1">{t.endDateText}</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                {(startDate || endDate) && (
                                    <button
                                        onClick={() => {
                                            setStartDate('');
                                            setEndDate('');
                                        }}
                                        className="md:col-span-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        {t.clearDateFilterText}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {filteredReceipts.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">{t.noReceiptsText}</div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {paginatedReceipts.map(receipt => (
                                    <div key={receipt.id}
                                         className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex gap-4">
                                            {/* Receipt Photo Thumbnail */}
                                            {receipt.image && (
                                                <div className="flex-shrink-0">
                                                    <img
                                                        src={`data:image/jpeg;base64,${receipt.image}`}
                                                        alt="Receipt"
                                                        className="w-20 h-20 object-cover rounded cursor-pointer border-2 border-gray-300 hover:border-indigo-500 transition-all"
                                                        onClick={() => setViewReceiptImage(receipt.image)}
                                                        title={t.clickToViewFullSizeText}
                                                    />
                                                </div>
                                            )}

                                            <div className="flex-1">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <div className="font-semibold text-gray-800">{receipt.store || t.noStoreNameText}</div>
                                                        <div className="text-sm text-gray-500 flex items-center gap-1">
                                                            <Calendar size={14}/>
                                                            {new Date(receipt.date).toLocaleDateString(COUNTRY_CONFIG[selectedCountry].dateFormat, {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-xl font-bold text-gray-800">
                                                            {currency}{receipt.items.reduce((sum, i) => sum + i.price, 0).toFixed(2)}
                                                        </div>
                                                        <div className="flex gap-2 mt-1">
                                                            <button
                                                                onClick={() => startEditingReceipt(receipt)}
                                                                className="text-blue-600 hover:text-blue-700 text-sm"
                                                                title={t.editReceiptText}
                                                            >
                                                                <Pencil size={16}/>
                                                            </button>
                                                            <button
                                                                onClick={() => deleteReceipt(receipt.id)}
                                                                className="text-red-600 hover:text-red-700 text-sm"
                                                                title={t.deleteReceiptText}
                                                            >
                                                                <Trash2 size={16}/>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    {receipt.items.map((item, idx) => (
                                                        <div key={idx} className="flex justify-between text-sm">
                                                            <span className="text-gray-600">
                                                                {item.name} <span className="text-gray-400">({item.category})</span>
                                                            </span>
                                                            <span className="font-medium text-gray-700">{currency}{item.price.toFixed(2)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 mt-6">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.previousText}
                                    </button>

                                    <div className="flex gap-1">
                                        {[...Array(totalPages)].map((_, idx) => {
                                            const pageNum = idx + 1;
                                            // Show first page, last page, current page, and pages around current
                                            if (
                                                pageNum === 1 ||
                                                pageNum === totalPages ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white'
                                                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            } else if (
                                                pageNum === currentPage - 2 ||
                                                pageNum === currentPage + 2
                                            ) {
                                                return <span key={pageNum}
                                                             className="px-2 py-2 text-gray-400">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                    >
                                        {t.nextText}
                                    </button>
                                </div>
                            )}

                            <div className="text-center text-sm text-gray-500 mt-4">
                                {t.showingText} {((currentPage - 1) * receiptsPerPage) + 1} {t.toText} {Math.min(currentPage * receiptsPerPage, filteredReceipts.length)} {t.ofText} {filteredReceipts.length} {t.receiptsText}
                            </div>
                        </>
                    )}
                </div>
                {/* Full-Size Image Viewer Modal */}
                {viewReceiptImage && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                        onClick={() => setViewReceiptImage(null)}
                    >
                        <div className="relative max-w-6xl w-full max-h-full flex flex-col items-center">
                            <div className="flex justify-between items-center w-full mb-4">
                                <h3 className="text-white text-lg font-semibold">Receipt Photo</h3>
                                <button
                                    onClick={() => setViewReceiptImage(null)}
                                    className="text-white hover:text-gray-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
                                >
                                    <X size={24}/>
                                </button>
                            </div>
                            <div className="overflow-auto max-h-[85vh] rounded-lg">
                                <img
                                    src={`data:image/jpeg;base64,${viewReceiptImage}`}
                                    alt="Receipt full size"
                                    className="max-w-full h-auto rounded-lg shadow-2xl"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div className="text-white text-sm mt-4 opacity-75">
                                Click outside to close
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}